import styled, { css } from 'styled-components';
import { ErrorMessage } from 'formik';

import { Caret } from 'shared/icons';
import { IMenuItem } from 'shared/types/MenuItem';
import { variableFontRegular } from 'styles/theme';

interface INativeDropdownMenuSelector {
  'data-qa': string;
  disabled?: boolean;
  displayValue?: string; // What we want to display as the selected value if not the menuItem.display
  hasFormikError?: boolean;
  label: string;
  menuItems: Array<IMenuItem>;
  name?: string;
  darkTheme?: boolean;
  onBlur?: () => void;
  onMenuItemClick: (menuItem: IMenuItem) => void;
  selection: IMenuItem;
}

/**
 * This Native Dropdown works by overlaying a transparent <select> element over the
 * visible field container, selection div, label, and error components.
 */
export const NativeDropdownMenuSelector = ({
  'data-qa': dataQa,
  disabled = false,
  displayValue,
  hasFormikError = false,
  label,
  menuItems,
  name,
  darkTheme,
  onBlur,
  onMenuItemClick,
  selection,
}: INativeDropdownMenuSelector) => {
  const handleChange = (e) => {
    const select = e.target;
    const indexExcludingDefault = select.selectedIndex - 1;

    const selectedItem = {
      ...menuItems?.[indexExcludingDefault],
      value: select.value,
      display: select.options[select.selectedIndex]?.text,
    };

    onMenuItemClick(selectedItem);
  };

  return (
    <NativeDropdownMenuSelector.Wrapper>
      <NativeDropdownMenuSelector.Select
        data-qa={`${dataQa}_select`}
        disabled={disabled}
        id="dropdown-select"
        name={name}
        onBlur={onBlur}
        onChange={handleChange}
        value={selection?.value}
      >
        <NativeDropdownMenuSelector.DisabledDefaultOption key="" value="" disabled />
        {menuItems.map(({ display, value }) => (
          <option key={value} value={value}>
            {display}
          </option>
        ))}
      </NativeDropdownMenuSelector.Select>
      <NativeDropdownMenuSelector.StyledSelect $darkTheme={darkTheme}>
        <NativeDropdownMenuSelector.Label
          isFloating={!!selection?.display}
          htmlFor="dropdown-select"
        >
          {label}
        </NativeDropdownMenuSelector.Label>
        <NativeDropdownMenuSelector.Selection
          $darkTheme={darkTheme}
          data-qa={`${dataQa}_selection`}
        >
          {displayValue || selection?.display}
        </NativeDropdownMenuSelector.Selection>
        <NativeDropdownMenuSelector.Caret $darkTheme={darkTheme} />
      </NativeDropdownMenuSelector.StyledSelect>
      {hasFormikError && (
        <NativeDropdownMenuSelector.Error
          name={name}
          component="span"
          data-qa={`${dataQa}_error`}
        />
      )}
    </NativeDropdownMenuSelector.Wrapper>
  );
};

NativeDropdownMenuSelector.Wrapper = styled.div`
  @media (hover: hover) and (pointer: fine) {
    display: none;
  }
`;

NativeDropdownMenuSelector.Select = styled.select`
  position: absolute;
  top: 0;
  left: 0;
  height: 60%;
  width: 100%;
  opacity: 0;
`;

NativeDropdownMenuSelector.StyledSelect = styled.div<{
  $darkTheme: INativeDropdownMenuSelector['darkTheme'];
}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0 8px;
  border-bottom: ${({ theme, $darkTheme }) =>
    $darkTheme ? theme.borders.white : theme.navigation.border};
`;

NativeDropdownMenuSelector.Label = styled.label<{ isFloating: boolean }>`
  ${variableFontRegular({ size: '11.5px', height: '14px' })};
  cursor: pointer;
  transition: 0.2s ease all;

  ${({ isFloating }) =>
    isFloating &&
    css`
      position: absolute;
      transform: translateY(-16px);
      ${variableFontRegular({ size: '9.5px', height: '12px' })};
    `};
`;

NativeDropdownMenuSelector.Selection = styled.div<{
  $darkTheme: INativeDropdownMenuSelector['darkTheme'];
}>`
  color: ${({ theme, $darkTheme }) => ($darkTheme ? theme.borders.white : theme.navigation.border)};
  ${variableFontRegular({ size: '11.5px', height: '14px' })};
`;

NativeDropdownMenuSelector.Error = styled(ErrorMessage)`
  color: ${({ theme }) => theme.colors.error};
  ${variableFontRegular({ size: '9.5px', height: '9px' })};
  margin-top: 8px;
`;

NativeDropdownMenuSelector.Caret = styled(Caret)<{
  $darkTheme: INativeDropdownMenuSelector['darkTheme'];
}>`
  path {
    stroke: ${({ theme, $darkTheme }) =>
      $darkTheme ? theme.colors.white : theme.navigation.textColor};
  }
`;

NativeDropdownMenuSelector.DisabledDefaultOption = styled.option`
  display: none;
`;
